<template>
  <SettingsPagePanel>
    <SettingsPagePanelHeader>
      <template #title>自動応答チャットメッセージ 条件の作成</template>
    </SettingsPagePanelHeader>
    <AnswerMessageAutoChatReplySettingTable :form="form" @save="saveSetting" />
  </SettingsPagePanel>
</template>

<script lang="ts" setup>
import AnswerMessageAutoChatReplySettingTable, {
  type AutoChatReplySettingTableForm,
} from './AnswerMessageAutoChatReplySettingTable.vue'
import { computed, ref } from 'vue'
import { useAlert } from '@/libs/alertPortal'
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router'
import { captureException } from '@sentry/vue'
import {
  useCreateAutoChatReplySettingMutation,
  type CreateAutoChatReplySettingMutation,
} from './SettingsPageAutoChatReplyCreateView.generated'
import { AutoChatReplySettingExtraType } from '@/types/schema.generated'
import { assertExhaustive } from '@/libs/utilities/assertExhaustive'
import { getQuery } from '@/libs/utilities/getQuery'
import SettingsPagePanel from '../SettingsPagePanel.vue'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import { useLogger } from '@/logger/logger'

const emit = defineEmits<{
  (
    event: 'onBeforeLeaveConfirmation',
    callbacks: { onConfirm: () => void; onAbort: () => void }
  ): void
}>()

const route = useRoute('autoChatReplySettingNew')

const keywordQuery = getQuery(route.query.keyword)

const isCompleted = ref(false)

const form = ref<AutoChatReplySettingTableForm>({
  keyword: keywordQuery ?? '',
  body: '',
  isEnabled: false,
  extra: {
    type: AutoChatReplySettingExtraType.None,
  },
})

const isEditing = computed({
  get(): boolean {
    return route.name === 'autoChatReplySettingNew'
  },
  set(value: boolean): void {
    const name = value ? 'autoChatReplySettingNew' : 'autoChatReplySetting'
    // eslint-disable-next-line fp/no-mutating-methods
    router.push({ name })
  },
})

const { mutate: createAutoChatReplySetting } =
  useCreateAutoChatReplySettingMutation({})

onBeforeRouteLeave((to, from, next) => {
  if (from.name !== 'autoChatReplySettingNew') {
    next()
    return
  }
  if (to.name === 'autoChatReplySetting' && !isCompleted.value) {
    next()
    return
  }
  emit('onBeforeLeaveConfirmation', {
    onConfirm: () => {
      isCompleted.value = true
      next()
    },
    onAbort: () => next(false),
  })
})

const logger = useLogger()

const handleSetting = async (
  setting: Maybe<
    CreateAutoChatReplySettingMutation['createAutoChatReplySetting']
  >
) => {
  if (!setting) {
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
    return
  }

  switch (setting.__typename) {
    case 'SaveAutoChatReplySettingSuccess':
      await logger.ltlPeople.event.createAutoChatReplySetting(
        logger.buildMeta(),
        {
          data: {
            id: Number(setting.autoChatReplySetting.id),
            body: setting.autoChatReplySetting.body,
            keyword: setting.autoChatReplySetting.keyword,
            isEnabled: setting.autoChatReplySetting.isEnabled,
            extra: setting.autoChatReplySetting.extra,
          },
        }
      )
      alert.success('自動応答メッセージを更新しました')
      return
    case 'AutoChatReplySettingDuplicateKeywordError':
      alert.error('自動応答する選択肢が他の設定と重複しています')
      return
    default:
      alert.error(
        'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
      )
  }
}

const alert = useAlert()
const router = useRouter()

const saveSetting = async (value: AutoChatReplySettingTableForm) => {
  try {
    const res = await createAutoChatReplySetting({
      input: {
        body: value.body,
        keyword: value.keyword,
        isEnabled: value.isEnabled,
        extra: buildInputExtra(value),
      },
    })

    await handleSetting(res?.data?.createAutoChatReplySetting)
    isEditing.value = false
    // eslint-disable-next-line fp/no-mutating-methods
    router.push({
      name: 'autoChatReplySetting',
    })
  } catch (e) {
    captureException(e)
    alert.error(
      'エラーが発生しました。再度お試しいただくか、サポートにお問い合わせください。'
    )
  }
}

const buildInputExtra = (form: AutoChatReplySettingTableForm) => {
  switch (form.extra.type) {
    case AutoChatReplySettingExtraType.None:
      return {
        type: AutoChatReplySettingExtraType.None,
      }
    case AutoChatReplySettingExtraType.Question:
      return {
        type: AutoChatReplySettingExtraType.Question,
        question: {
          selections: form.extra.selections,
        },
      }
    case AutoChatReplySettingExtraType.BookingRequest:
      return {
        type: AutoChatReplySettingExtraType.BookingRequest,
        bookingRequest: {
          availableTimeSlotRange: form.extra.availableTimeSlotRange,
          fallbackUrl: form.extra.fallbackUrl,
        },
      }
    default:
      return assertExhaustive(form.extra)
  }
}
</script>
